import {
  Component,
  computed,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';

import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { User } from '../../../../models/user.model';
import { getCurrentUser, State } from '../../../../store';
import { MobileNavService } from '../../../../core/services/mobile-nav';
import { Navigation } from '../navigation-record/navigation-record.types';

@Component({
  selector: 'app-mobile-nav-panel',

  templateUrl: './mobile-nav-panel.component.html',

  standalone: false,
})
export class MobileNavPanelComponent implements OnInit {
  @Input({ required: true }) rootItems: Navigation.Record[] = [];

  @Input({ required: true }) currentItems: Navigation.Record[] = [];

  @Input({ required: true }) currentTitle = '';

  @Input() stackDepth = 0;

  @Input() initialShowAccountSwitcher = false;

  @Output() rootSelect = new EventEmitter<Navigation.Record>();

  @Output() itemNavigate = new EventEmitter<Navigation.Record>();

  @Output() itemLinkNavigate = new EventEmitter<void>();

  @Output() itemAction = new EventEmitter<string>();

  @Output() backNav = new EventEmitter<void>();

  @Output() closeNav = new EventEmitter<void>();

  private readonly store = inject(Store<State>);
  private readonly mobileNavService = inject(MobileNavService);

  readonly user = toSignal<User>(this.store.select(getCurrentUser));

  readonly accountName = computed<string>(() => {
    const user = this.user();

    return user?.Account?.Name ?? '';
  });

  readonly billingAddress = computed<string>(() => {
    const user = this.user();

    if (!user?.Account?.BillingAddress) return '';

    const { street, city, stateCode, postalCode } = user.Account
      .BillingAddress as Record<string, string>;

    return `${street ?? ''}\r\n${city ?? ''}, ${stateCode ?? ''} ${postalCode ?? ''}`;
  });

  showAccountSwitcher = this.mobileNavService.showAccountSwitcher;

  ngOnInit(): void {
    if (this.initialShowAccountSwitcher) {
      this.showAccountSwitcher.set(true);
    }
  }

  accounts = computed(() => (this.user() as any)?.Accounts || []);

  hasMultipleAccounts = computed(() => this.accounts().length > 1);

  isActiveRoot(item: Navigation.Record): boolean {
    return (item.label || item.alias) === this.currentTitle;
  }

  openAccountSwitcher(): void {
    this.mobileNavService.openAccountSwitcherFromNav();
  }

  closeAccountSwitcher(): void {
    const openedFromDesktop = this.mobileNavService.accountSwitcherOpenedFromDesktop();
    this.mobileNavService.closeAccountSwitcher();
    // Only close entire panel if it was opened from the desktop widget
    if (openedFromDesktop && this.stackDepth === 0) {
      this.closeNav.emit();
    }
  }

  onAccountSelected(accountId: string): void {
    // TODO: Implement account switching logic
    // This should update the logged-in user's current account
    this.closeAccountSwitcher();
  }
}
